import lodash from "lodash";
import { IAddressInfoState } from "@/main/components/shared/addressInfo";
import { formattedContactGet, IContactInfoState } from "@/main/components/shared/contactInfo";
import { BodyshopRegistrationDataResponse, PreQuoteResponse } from "@/main/models/response";
import { isEmpty, isNullUndefined } from "@/main/services/utils";
import { IKeysPreQuoteFormGlass, IKeysPreQuoteFormTire, IPreQuoteForm } from "./context";

type TypeSectionsPrequote = "bodyshop" | "glass" | "tire" | "vehicle";

type TypeFieldsVehicle = Pick<IPreQuoteForm, "vehicleCountry" | "manufacturer" | "model" | "trim" | "year">;
type TypeFieldsGlass = Pick<IPreQuoteForm, "glasses">;
type TypeFieldsTire = Pick<IPreQuoteForm, "tires">;
type TypeFieldsBodyshop = Pick<IPreQuoteForm, "country" | "companyName" | "rfc" | "tradeName"> & {
	contacts: IContactInfoState[];
	address: IAddressInfoState[];
};
interface IFieldChange {
	nameField: string;
	oldValue: unknown;
	newValue: unknown;
}

export const checkPendingGlass = (preQuoteData: PreQuoteResponse | null) =>
	isNullUndefined(preQuoteData?.preQuoteGlass)
		? false
		: Boolean(preQuoteData?.preQuoteGlass?.some((i) => i.glassPending));
export const checkPendingTire = (preQuoteData: PreQuoteResponse | null) =>
	isNullUndefined(preQuoteData?.preQuoteTire) ? false : Boolean(preQuoteData?.preQuoteTire?.some((i) => i.tirePending));

export function generateKeyValueVehicle(guid: string, value: string) {
	if (!guid || !value) return null;

	return {
		key: guid,
		value: value,
	};
}

// #region Vehicles
export function backendForFrontendVehicle(dataAPI: PreQuoteResponse): TypeFieldsVehicle {
	const manufacturer = generateKeyValueVehicle(dataAPI.vehicleGuidManufacturer, dataAPI.vehicleManufacturer);
	const model = generateKeyValueVehicle(dataAPI.vehicleGuidVehicleModel, dataAPI.vehicleModel);
	const trim = generateKeyValueVehicle(dataAPI.vehicleGuidSubModel, dataAPI.vehicleSubModel);
	const year = generateKeyValueVehicle(dataAPI.vehicleGuidYear, dataAPI.vehicleYear);
	const vehicleCountry = generateKeyValueVehicle(dataAPI.vehicleGuidCountry ?? "", dataAPI.vehicleCountry ?? "");

	return {
		vehicleCountry,
		manufacturer,
		model,
		trim,
		year,
	};
}

function hasChangedVehicle(dataAPI: PreQuoteResponse, dataScreen: IPreQuoteForm): IFieldChange[] {
	const formattedDataAPI = backendForFrontendVehicle(dataAPI);
	const changes: IFieldChange[] = [];
	const keysFormVehicle: (keyof TypeFieldsVehicle)[] = ["manufacturer", "model", "trim", "year"];

	keysFormVehicle.forEach((key) => {
		const valueAPI = formattedDataAPI[key];
		const valueScreen = dataScreen[key];

		if (
			valueAPI?.key !== valueScreen?.key ||
			valueAPI?.value !== valueScreen?.value ||
			dataScreen.isChangedVehicleForm
		) {
			changes.push({
				nameField: key,
				oldValue: valueAPI,
				newValue: valueScreen,
			});
		}
	});

	return changes;
}
//#endregion

// #region Glass
export function backendForFrontendGlass(dataAPI: PreQuoteResponse): TypeFieldsGlass {
	if (isNullUndefined(dataAPI.preQuoteGlass)) return { glasses: [] };

	const glassFieds: TypeFieldsGlass = {
		glasses: dataAPI.preQuoteGlass.map((glass) => ({
			guidQuoteItem: glass.guidQuoteItem,
			code: "",
			partnumber: glass.notes ? `${glass.partInfo} [${glass.notes}]` : glass.partInfo,
			refPrice: isEmpty(glass.priceCap) ? 0 : Number(glass.priceCap),
			notes: glass.notes,
		})),
	};

	return glassFieds;
}
function hasChangedGlass(dataAPI: PreQuoteResponse, dataScreen: IPreQuoteForm): IFieldChange[] {
	const formattedDataAPI = backendForFrontendGlass(dataAPI);
	const changes: IFieldChange[] = [];

	dataScreen.glasses.forEach((glass, index) => {
		const glassAPI = formattedDataAPI.glasses.find((i) => i.guidQuoteItem === glass.guidQuoteItem);

		if (!glassAPI) {
			changes.push({
				nameField: `glasses.${index}`,
				oldValue: glassAPI,
				newValue: glass,
			});
			return;
		}

		Object.entries(glass).forEach(([key, value]) => {
			const keyTyped = key as IKeysPreQuoteFormGlass;
			if (value?.toString() !== glassAPI[keyTyped]?.toString()) {
				changes.push({
					nameField: `glasses.${index}.${keyTyped}`,
					oldValue: glassAPI[keyTyped],
					newValue: value,
				});
			}
		});
	});

	return changes;
}
//#endregion

// #region Tires
export function backendForFrontendTire(dataAPI: PreQuoteResponse): TypeFieldsTire {
	if (isNullUndefined(dataAPI.preQuoteTire)) return { tires: [] };

	const tireFieds: TypeFieldsTire = {
		tires: dataAPI.preQuoteTire.map((tire) => ({
			guidQuoteItem: tire.guidQuoteItem,
			range: "",
			rim: "",
			brand: null,
			height: "",
			model: "",
			width: "",
			partnumber: tire.notes ? `${tire.partInfo} [${tire.notes}]` : tire.partInfo,
			refPrice: isEmpty(tire.priceCap) ? 0 : Number(tire.priceCap),
			notes: tire.notes,
		})),
	};

	return tireFieds;
}
function hasChangedTire(dataAPI: PreQuoteResponse, dataScreen: IPreQuoteForm) {
	const formattedDataAPI = backendForFrontendTire(dataAPI);
	const changes: IFieldChange[] = [];

	dataScreen.tires.forEach((tire, index) => {
		const tireAPI = formattedDataAPI.tires.find((i) => i.guidQuoteItem === tire.guidQuoteItem);

		if (!tireAPI) {
			changes.push({
				nameField: `tires.${index}`,
				oldValue: tireAPI,
				newValue: tire,
			});
			return;
		}

		Object.entries(tire).forEach(([key, value]) => {
			const keyTyped = key as IKeysPreQuoteFormTire;
			if (value !== tireAPI[keyTyped]) {
				changes.push({
					nameField: `tires.${index}.${keyTyped}`,
					oldValue: tireAPI[keyTyped],
					newValue: value,
				});
			}
		});
	});

	return changes;
}
//#endregion

// #region Bodyshop
export function backendForFrontendBodyshop(dataAPI: BodyshopRegistrationDataResponse | null): TypeFieldsBodyshop {
	if (isNullUndefined(dataAPI)) {
		return {
			country: null,
			companyName: "",
			rfc: "",
			tradeName: "",
			contacts: [],
			address: [],
		};
	}
	const country = generateKeyValueVehicle(dataAPI.country.guid, dataAPI.country.name);
	const companyName = dataAPI.companyName;
	const rfc = dataAPI.registrationNumber;
	const tradeName = dataAPI.tradeName;
	const contacts: IContactInfoState[] = dataAPI.contacts.map((contact) => formattedContactGet(contact));
	const address = dataAPI.addresses.map((address) => ({
		id: address.guid,
		address: address.street,
		complement: address.complement,
		number: address.number === "" ? null : address.number,
		zipCode: address.municipality.zipCode,
		isDeliveryAddress: String(address.delivery).toLowerCase() === "true", // check this field
		addressType: {
			key: address.addressType.idAddressType,
			value: address.addressType.name,
		},
		city: {
			key: address.municipality.city.guid,
			value: address.municipality.city.name,
		},
		municipality: {
			key: address.municipality.guid,
			value: address.municipality.name,
		},
		state: {
			key: address.municipality.city.state.guid,
			value: address.municipality.city.state.name,
		},
	})) as IAddressInfoState[];

	return {
		companyName,
		country,
		rfc,
		tradeName,
		contacts,
		address,
	};
}
function hasChangedBodyshop(
	dataScreen: IPreQuoteForm,
	dataBodyshop: BodyshopRegistrationDataResponse | null,
	dataContacts: IContactInfoState[],
	dataAddress: IAddressInfoState[],
): IFieldChange[] {
	const formattedDataAPI = backendForFrontendBodyshop(dataBodyshop);
	const changes: IFieldChange[] = [];
	const keysFormBodyshop: (keyof Omit<TypeFieldsBodyshop, "contacts" | "address">)[] = [
		"companyName",
		"country",
		"rfc",
		"tradeName",
	];

	keysFormBodyshop.forEach((key) => {
		const valueAPI = formattedDataAPI[key];
		const valueScreen = dataScreen[key];

		if (valueAPI?.toString() !== valueScreen?.toString()) {
			changes.push({
				nameField: key,
				oldValue: valueAPI,
				newValue: valueScreen,
			});
		}
	});

	//#region Contacts
	type KeysIContactInfoState = keyof IContactInfoState;
	dataContacts.forEach((contact, index) => {
		const contactAPI = formattedDataAPI.contacts.find((i) => i.id === contact.id);

		if (!contactAPI) {
			changes.push({
				nameField: `contact.${index}`,
				oldValue: contactAPI,
				newValue: contact,
			});
			return;
		}

		Object.entries(contact).forEach(([key, value]) => {
			const keyTyped = key as KeysIContactInfoState;
			// if (value !== contactAPI[keyTyped]) {
			if (!lodash.isEqual(value, contactAPI[keyTyped])) {
				changes.push({
					nameField: `contact.${index}.${keyTyped}`,
					oldValue: contactAPI[keyTyped],
					newValue: value,
				});
			}
		});
	});
	//#endregion

	//#region Address
	type KeysIAddressInfoState = keyof IAddressInfoState;
	dataAddress.forEach((address, index) => {
		const addressAPI = formattedDataAPI.address.find((i) => i.id === address.id);

		if (!addressAPI) {
			changes.push({
				nameField: `address.${index}`,
				oldValue: addressAPI,
				newValue: address,
			});
			return;
		}

		Object.entries(address).forEach(([key, value]) => {
			const keyTyped = key as KeysIAddressInfoState;
			if (!lodash.isEqual(value, addressAPI[keyTyped])) {
				changes.push({
					nameField: `address.${index}.${keyTyped}`,
					oldValue: addressAPI[keyTyped],
					newValue: value,
				});
			}
		});
	});
	//#endregion

	return changes;
}
//#endregion

interface ICheckHasPreQuoteFormChangeProps {
	dataAPI: PreQuoteResponse | null;
	dataScreen: IPreQuoteForm;
	bodyshopData: BodyshopRegistrationDataResponse | null;
	dataContacts: IContactInfoState[];
	dataAddress: IAddressInfoState[];
}

export interface ICheckHasPreQuoteFormChangeResponse {
	hasChange: boolean;
	sectionsChanged: Partial<Record<TypeSectionsPrequote, IFieldChange[]>>;
}
const INITIAL_CHANGES_PRE_QUOTE: ICheckHasPreQuoteFormChangeResponse = {
	hasChange: false,
	sectionsChanged: {},
};
export function checkHasPreQuoteFormChange({
	dataAPI: dataAPIProp,
	dataScreen: dataScreenProp,
	bodyshopData: bodyshopDataProp,
	dataAddress: dataAddressProp,
	dataContacts: dataContactsProp,
}: ICheckHasPreQuoteFormChangeProps): ICheckHasPreQuoteFormChangeResponse {
	if (isNullUndefined(dataAPIProp) || isNullUndefined(dataScreenProp))
		return lodash.cloneDeep(INITIAL_CHANGES_PRE_QUOTE);

	const result: ICheckHasPreQuoteFormChangeResponse = lodash.cloneDeep(INITIAL_CHANGES_PRE_QUOTE);

	const dataAPI = lodash.cloneDeep(dataAPIProp);
	const dataScreen = lodash.cloneDeep(dataScreenProp);
	const bodyshopData = lodash.cloneDeep(bodyshopDataProp);
	const dataAddress = lodash.cloneDeep(dataAddressProp);
	const dataContacts = lodash.cloneDeep(dataContactsProp);

	const changesVehicle = hasChangedVehicle(dataAPI, dataScreen);
	const changesGlass = hasChangedGlass(dataAPI, dataScreen);
	const changesTire = hasChangedTire(dataAPI, dataScreen);
	const changesBodyshop = hasChangedBodyshop(dataScreen, bodyshopData, dataContacts, dataAddress);

	const sections: { key: TypeSectionsPrequote; changes: IFieldChange[] }[] = [
		{ key: "vehicle", changes: changesVehicle },
		{ key: "glass", changes: changesGlass },
		{ key: "tire", changes: changesTire },
		{ key: "bodyshop", changes: changesBodyshop },
	];

	sections.forEach((section) => {
		if (section.changes.length > 0) {
			Object.assign(result.sectionsChanged, {
				[section.key]: section.changes,
			});
		}
	});

	result.hasChange = sections.some((section) => section.changes.length > 0);

	return result;
}

export function checkHasChangedForm(
	nameSection: TypeSectionsPrequote,
	dataFormChanges: ICheckHasPreQuoteFormChangeResponse,
) {
	return Object.keys(dataFormChanges.sectionsChanged).includes(nameSection);
}
